import { forwardRef, memo, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { CaretDownIcon, CheckSmallIcon } from '@/icons';
import { useUpsellingGuard } from '@/shared/plan';
import { Button, type ButtonProps, Dropdown } from '@/ui_v3';
import { useEditMode } from '../../focus/useIsEditMode';
import { type QuizType } from '../../type';
import { useQuizFormField } from '../hook/useQuizFormField';
import { QUIZ_TYPE_ICON_MAP } from './QuizTypeIcons';
import { QuizTypeSelectorOption } from './QuizTypeSelectorOption';
const QUIZ_TYPES: QuizType[] = ['multiple', 'subjective', 'ox', 'essay', 'match'];
export const QuizTypeSelector = memo(() => {
  const [open, setOpen] = useState(false);
  const {
    onEditModeChange
  } = useEditMode();
  const [type, updateType] = useQuizFormField({
    field: 'type'
  });
  const upsellingGuard = useUpsellingGuard();
  const handleTypeSelect = (nextType: QuizType) => {
    if (nextType === 'match') {
      upsellingGuard(() => updateType(nextType), {
        feature: 'MATCH',
        from: 'match'
      });
      return;
    }
    updateType(nextType);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    onEditModeChange(nextOpen);
  };
  return <Dropdown.Root modal={false} open={open} onOpenChange={handleOpenChange}>
      <Dropdown.Trigger asChild onClick={e => {
      e.stopPropagation();
    }} onFocus={e => {
      e.preventDefault();
      e.stopPropagation();
    }} onMouseDown={e => {
      e.preventDefault();
      e.stopPropagation();
    }}>
        <QuizTypeSelectorButton className="group text-body-sm-bold" selectedType={type} rightSlot={<CaretDownIcon size="xs" className="group-data-[state=open]:rotate-180" />} />
      </Dropdown.Trigger>
      <Dropdown.Content className="w-[180px] py-3xs px-xs" align="start" autoFocus={false} onFocus={e => {
      e.preventDefault();
      e.stopPropagation();
    }} onEscapeKeyDown={event => {
      event.preventDefault();
      handleOpenChange(false);
    }}>
        {QUIZ_TYPES.map(t => <Dropdown.Item key={t} checked={type === t} onSelect={() => handleTypeSelect(t)} className="justify-between">
            <QuizTypeSelectorOption type={t} />
            {type === t && <CheckSmallIcon size="xs" />}
          </Dropdown.Item>)}
      </Dropdown.Content>
    </Dropdown.Root>;
});
type QuizTypeSelectorButtonProps = {
  selectedType: QuizType;
} & Omit<ButtonProps, 'asChild'>;
export const QuizTypeSelectorButton = forwardRef<HTMLButtonElement, QuizTypeSelectorButtonProps>(({
  selectedType,
  ...rest
}, ref) => {
  const {
    t
  } = useTranslation(['build', 'common']);
  return <Button ref={ref} variant={'contained'} intent="neutral" size="xs" leftSlot={QUIZ_TYPE_ICON_MAP[selectedType]} asChild={false} {...rest}>
      {selectedType === 'match' ? t('common:quizCard.typeBadge.match') : t(`build.quizType.${selectedType}`)}
    </Button>;
});